/**
 * BBVision theme tokens (single source of truth).
 *
 * - Keep in sync with `docs/BBVISION_THEME.md`
 * - This file is meant to be imported by theme foundations (colors/semanticTokens)
 *   and can also be imported by pages/components when needed.
 */
export const BBVISION_THEME = {
  colors: {
    text: {
      primary: { light: '#21201C', dark: '#FFFFFDEC' },
      secondary: { light: '#646464', dark: '#FFFFFFA9' },
      // align with `docs/BBVISION_THEME.md`
      // - light: brown accent
      // - dark: yellow accent
      accent: { light: '#915930', dark: '#FFCC4D' },
      accentHover: { light: '#915930', dark: '#FFCC4D' },
      strong: { light: '#202020', dark: '#FFFFFDEC' },
      muted: { light: '#00000066', dark: '#FDF6ED80' },
      state: {
        down: { light: '#C62A2F', dark: '#FF8589' },
        up: { light: '#18794E', dark: '#3DD68C' },
        warning: { light: '#915930', dark: '#FFB84D' },
      },
    },
    bg: {
      body: { light: '#F9F9F8', dark: '#0C0C0C' },
      primary: { light: '#FCFCFC', dark: '#282828' },
      secondary: { light: '#F5F2E9', dark: '#FFFFFF0D' },
      secondarySubtle: { light: '#F1F1F1', dark: '#FFFFFF0D' },
      overlaySubtle: { light: '#FCFCFC', dark: '#FFFFFF12' },
      skeleton: { light: '#F2F2F0', dark: '#FFFFFF0D' },
      input: { light: '#FFFFFF', dark: '#FFFFFF0D' },
      stepIndicator: { light: '#915930', dark: '#FFCC4D' },
      state: {
        success: { light: '#18794E', dark: '#3DD68C' },
        successSubtle: { light: '#18794E0D', dark: '#3DD68C0D' },
        error: { light: '#C62A2F', dark: '#FF8589' },
        errorSubtle: { light: '#C62A2F0D', dark: '#FF85890D' },
        warningSubtle: { light: '#9159300D', dark: '#FFCD4D13' },
        warningLight: { light: '#FFF3D0', dark: '#FFCD4D13' },
        active: { light: '#299764', dark: '#299764' },
        exited: { light: '#D93D42', dark: '#D93D42' },
      },
    },
    border: {
      'default': { light: '#0000001A', dark: '#FFFFFF1A' },
      state: {
        success: { light: '#18794E', dark: '#3DD68C' },
        error: { light: '#C62A2F', dark: '#FF8589' },
        warning: { light: '#E4BB78', dark: '#C7A140' },
      },
      input: {
        focus: { light: '#BBA28B', dark: '#CFB024' },
      },
    },
    table: {
      rowHoverBg: { light: '#F1F1F1', dark: '#FAFAFA0D' },
      rowDivider: { light: '#0000000D', dark: '#FFFFFF1A' },
    },
    tooltip: {
      bg: { light: '#FFFFFF', dark: '#1C1C19' },
    },
    icon: {
      toast: { light: '#FFC53D', dark: '#FFC53D' },
    },
    shadow: {
      sm: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px',
      card: '0 0 2px rgba(0,0,0,0.15), 0 2px 5px rgba(0,0,0,0.05), 0 8px 40px rgba(0,0,0,0.04)',
    },
  },
  textStyles: {

    /**
     * Desktop / default viewport (measured).
     */
    desktop: {
      h1: { fontSize: '30px', lineHeight: '36px', fontWeight: 600 },
      sectionTitle: { fontSize: '16px', lineHeight: '20px', fontWeight: 500 },
      body: { fontSize: '16px', lineHeight: '24px', fontWeight: 500 },
      label: { fontSize: '12px', lineHeight: '16px', fontWeight: 500 },
      cta: { fontSize: '14px', lineHeight: '20px', fontWeight: 500 },
    },

    /**
     * Mobile viewport (< 768px, measured).
     */
    mobile: {
      h1: { fontSize: '24px', lineHeight: '32px', fontWeight: 600 },
      sectionTitle: { fontSize: '16px', lineHeight: '20px', fontWeight: 500 },
      body: { fontSize: '16px', lineHeight: '24px', fontWeight: 500 },
      label: { fontSize: '12px', lineHeight: '16px', fontWeight: 500 },
      cta: { fontSize: '14px', lineHeight: '20px', fontWeight: 500 },
    },
    fonts: {
      base: 'Geist, sans-serif',
      mono: '"Roboto Mono", monospace',
    },
  },
} as const;
